import { z } from 'zod';

export const DEPRECIATION_METHODS = ['straight_line'] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

export const DEPRECIATION_CONVENTIONS = ['full_month', 'mid_month'] as const;

/**
 * UI presets that fill in useful life + convention. Only the convention is
 * stored; the class is a shortcut, not a field. Real property is depreciated
 * to zero (no salvage) over 27.5 / 39 years under the IRS mid-month convention.
 */
export const PROPERTY_CLASSES = {
  general: { label: 'Equipment, vehicles, furniture', usefulLifeMonths: null, convention: 'full_month' },
  residential_rental: { label: 'Residential rental building (27.5 years)', usefulLifeMonths: 330, convention: 'mid_month' },
  nonresidential: { label: 'Nonresidential building (39 years)', usefulLifeMonths: 468, convention: 'mid_month' },
} as const satisfies Record<string, { label: string; usefulLifeMonths: number | null; convention: (typeof DEPRECIATION_CONVENTIONS)[number] }>;
export type PropertyClass = keyof typeof PROPERTY_CLASSES;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const nonNegMoney = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const positiveMoney = nonNegMoney.refine((v) => Number(v) > 0, 'must be greater than zero');

export const createFixedAssetInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  assetAccountId: z.string().uuid(),
  accumDepAccountId: z.string().uuid(),
  depExpenseAccountId: z.string().uuid(),
  cost: positiveMoney,
  salvageValue: nonNegMoney.default('0'),
  inServiceDate: isoDate,
  usefulLifeMonths: z.number().int().min(1).max(1200),
  method: z.enum(DEPRECIATION_METHODS).default('straight_line'),
  convention: z.enum(DEPRECIATION_CONVENTIONS).default('full_month'),
  /** Post each month's depreciation automatically once the month has closed (nightly job). */
  autoPost: z.boolean().default(false),
});
export type CreateFixedAssetInput = z.input<typeof createFixedAssetInputSchema>;

/**
 * Edit an existing fixed asset. Any field can be changed. Fields that affect
 * the depreciation schedule (cost, salvage, useful life, in-service date,
 * method) or the accounts posted to (assetAccountId, accumDepAccountId,
 * depExpenseAccountId) require `override: true` when any depreciation has
 * already been posted, and will:
 *   1. Void every posted depreciation JE for this asset (via the JE
 *      override path so recon locks don't block).
 *   2. Reset accumulatedDepreciation to 0 and lastDepreciatedThrough to
 *      null so the schedule can be re-derived.
 * `overrideReason` is required whenever `override` is set — it's logged
 * for audit alongside the void JEs.
 */
export const updateFixedAssetInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(500).nullable().optional(),
  assetAccountId: z.string().uuid().optional(),
  accumDepAccountId: z.string().uuid().optional(),
  depExpenseAccountId: z.string().uuid().optional(),
  cost: positiveMoney.optional(),
  salvageValue: nonNegMoney.optional(),
  inServiceDate: isoDate.optional(),
  usefulLifeMonths: z.number().int().min(1).max(1200).optional(),
  method: z.enum(DEPRECIATION_METHODS).optional(),
  convention: z.enum(DEPRECIATION_CONVENTIONS).optional(),
  autoPost: z.boolean().optional(),
  override: z.boolean().optional(),
  overrideReason: z.string().min(1).max(500).optional(),
});
export type UpdateFixedAssetInput = z.input<typeof updateFixedAssetInputSchema>;

export const runDepreciationInputSchema = z.object({ throughDate: isoDate });
export type RunDepreciationInput = z.infer<typeof runDepreciationInputSchema>;

export const disposeFixedAssetInputSchema = z.object({
  date: isoDate,
  proceeds: nonNegMoney.default('0'),
  /** Bank/cash account that receives the proceeds. */
  cashAccountId: z.string().uuid(),
  /** Account for the gain/loss on disposal; defaults to a system "Gain/Loss on Disposal". */
  gainLossAccountId: z.string().uuid().nullable().optional(),
});
export type DisposeFixedAssetInput = z.input<typeof disposeFixedAssetInputSchema>;

export interface DepreciationPeriod {
  periodDate: string;
  amount: string;
  accumulated: string;
  bookValue: string;
  posted: boolean;
}

export interface FixedAsset {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  assetAccountId: string;
  accumDepAccountId: string;
  depExpenseAccountId: string;
  cost: string;
  salvageValue: string;
  inServiceDate: string;
  usefulLifeMonths: number;
  method: DepreciationMethod;
  convention: (typeof DEPRECIATION_CONVENTIONS)[number];
  autoPost: boolean;
  accumulatedDepreciation: string;
  bookValue: string; // derived: cost − accumulatedDepreciation
  lastDepreciatedThrough: string | null;
  status: 'active' | 'disposed' | 'fully_depreciated';
  disposedDate: string | null;
  disposalProceeds: string | null;
  disposalJournalEntryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FixedAssetWithSchedule extends FixedAsset {
  schedule: DepreciationPeriod[];
}

export interface RunDepreciationResult {
  posted: number; // periods posted
  amount: string; // total depreciation posted
  journalEntryIds: string[];
}

/** One asset's outcome from the nightly auto-post sweep. */
export interface AutoDepreciationOutcome {
  companyId: string;
  fixedAssetId: string;
  name: string;
  posted: number;
  amount: string;
  error: string | null;
}
